//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { pluginService } from '@/services';
import { ADMIN_LIST_STALE_MS } from '@/services/adminQueries';
import { type ActivePlugin, type AdminPlugin } from '@/services/apiTypes';

// Query keys and options for Mission Control plugin resources.  Kept in their
// own namespace (not xgtKeys) because these hit the MC backend
// (/api/plugins/* and /api/admin/plugins), not xGT server state.

export const pluginKeys = {
  all: ['plugins'] as const,
  surfaces: () => [...pluginKeys.all, 'surfaces'] as const,
  admin: () => [...pluginKeys.all, 'admin'] as const
};

// The admin control-panel view: every loaded plugin with its
// active/available/blocked_by state.  Admin-only (403 for non-admins lands in
// the query's error).
export const adminPluginsQuery = () => ({
  queryKey: pluginKeys.admin(),
  queryFn: (): Promise<AdminPlugin[]> => pluginService.getAdminPlugins(),
  staleTime: ADMIN_LIST_STALE_MS,
  // Fresh on every opening of the pane; the stale window only holds off the
  // focus refetch while it stays open.
  refetchOnMount: 'always' as const
});

// The current user's available plugin surfaces (the gear-menu entries), read by
// the Header and invalidated by the admin plugin toggle.
export const pluginSurfacesQuery = () => ({
  queryKey: pluginKeys.surfaces(),
  queryFn: (): Promise<ActivePlugin[]> => pluginService.getPluginSurfaces(),
  // Surfaces change only when an admin toggles a plugin, and this session's
  // own toggle invalidates explicitly, so the same freshness window as the
  // admin lists keeps the client's focus-refetch off the manifest.
  staleTime: ADMIN_LIST_STALE_MS
});

// Single owner of the plugin-surfaces invalidation: the admin toggle calls this
// so the gear menu re-derives.
export const useInvalidatePluginSurfaces = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: pluginKeys.surfaces() }),
    [queryClient]);
};
